<template>
  <div class="report-page">

    <!-- ── Header ── -->
    <div class="report-header">
      <div class="header-left mt-4">
        <h1 class="header-title">IPD Click Report</h1>
      </div>
      <button class="export-btn" @click="exportExcel" :disabled="exporting">
        <svg v-if="!exporting" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
          <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
        </svg>
        <span v-if="exporting" class="spinner-sm"></span>
        {{ exporting ? 'Exporting...' : 'Export Excel' }}
      </button>
    </div>

    <!-- ── Filters ── -->
    <div class="filters-bar">
      <div class="filter-group">
        <label class="filter-label">From</label>
        <input type="date" class="filter-input" v-model="filters.from" @change="fetchAll" />
      </div>
      <div class="filter-group">
        <label class="filter-label">To</label>
        <input type="date" class="filter-input" v-model="filters.to" @change="fetchAll" />
      </div>
      <div class="filter-group">
        <label class="filter-label">Product</label>
        <select class="filter-input" v-model="filters.product_code" @change="fetchReport">
          <option value="">All Products</option>
          <option v-for="p in productOptions" :key="p.code" :value="p.code">{{ p.name }}</option>
        </select>
      </div>
      <button class="reset-btn" @click="resetFilters">Reset</button>
    </div>

    <!-- ── Stat Cards ── -->
    <div class="stats-grid" v-if="!statsLoading">
      <div class="stat-card" v-for="(stat, i) in stats" :key="stat.product_code" :style="{ '--accent': cardAccents[i % cardAccents.length] }">
        <div class="stat-top">
          <span class="stat-product">{{ stat.product_name }}</span>
          <span class="stat-code">{{ stat.product_code }}</span>
        </div>
        <div class="stat-number">{{ stat.total_clicks.toLocaleString() }}</div>
        <div class="stat-label">Total Clicks</div>
        <div class="stat-meta">
          <span>{{ stat.unique_ips }} unique IPs</span>
          <span>Last: {{ stat.last_click }}</span>
        </div>
      </div>
      <div class="stat-card stat-card--total" style="--accent: #067169">
        <div class="stat-top">
          <span class="stat-product">All Products</span>
          <span class="stat-code">combined</span>
        </div>
        <div class="stat-number">{{ totalClicks.toLocaleString() }}</div>
        <div class="stat-label">Total Clicks</div>
        <div class="stat-meta">
          <span>{{ stats.length }} products tracked</span>
        </div>
      </div>
    </div>
    <div class="stats-skeleton" v-else>
      <div class="skeleton-card" v-for="n in 4" :key="n"></div>
    </div>

    <!-- ── Data Table ── -->
    <div class="table-section">
      <div class="table-header">
        <h2 class="table-title">Click Log</h2>
        <span class="table-count" v-if="pagination.total">
          {{ pagination.total.toLocaleString() }} records
        </span>
      </div>

      <div class="table-wrap">
        <div class="table-loading-overlay" v-if="reportLoading">
          <span class="spinner-lg"></span>
        </div>

        <table class="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Product</th>
              <th>Clicked At</th>
              <th>IP Address</th>
              <th>Language</th>
              <th>User</th>
              <th>User Agent</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="rows.length === 0 && !reportLoading">
              <td colspan="7" class="empty-row">No records found for the selected filters.</td>
            </tr>
            <tr v-for="(row, index) in rows" :key="row.id" :class="{ 'row-even': index % 2 === 0 }">
              <td class="td-num">{{ (pagination.current_page - 1) * pagination.per_page + index + 1 }}</td>
              <td>
                <span class="product-badge">{{ row.product_code }}</span>
              </td>
              <td class="td-date">{{ formatDate(row.created_at) }}</td>
              <td class="td-mono">{{ row.ip_address || '—' }}</td>
              <td>
                <span class="lang-badge">{{ row.lang || '—' }}</span>
              </td>
              <td>
                <span v-if="row.user" class="user-info">
                  <span class="user-name">{{ row.user.name }}</span>
                  <span class="user-email">{{ row.user.email }}</span>
                </span>
                <span v-else class="guest-label">Guest</span>
              </td>
              <td class="td-agent">{{ truncate(row.user_agent, 50) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ── Pagination ── -->
      <div class="pagination-bar" v-if="pagination.last_page > 1">
        <button class="page-btn" :disabled="pagination.current_page === 1" @click="changePage(pagination.current_page - 1)">
          ‹ Prev
        </button>
        <span v-for="(page, index) in visiblePages" :key="index">
          <span v-if="page === '...'" class="page-ellipsis">…</span>
          <button v-else class="page-btn" :class="{ 'page-btn--active': page === pagination.current_page }" @click="changePage(page)">
            {{ page }}
          </button>
        </span>
        <button class="page-btn" :disabled="pagination.current_page === pagination.last_page" @click="changePage(pagination.current_page + 1)">
          Next ›
        </button>
      </div>

    </div>

    <!-- ── Toast ── -->
    <transition name="toast-fade">
      <div v-if="toast.show" class="toast" :class="'toast--' + toast.type">
        {{ toast.message }}
      </div>
    </transition>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "IpdReport",

  data() {
    const today = new Date().toISOString().slice(0, 10);
    const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString().slice(0, 10);

    return {
      filters: {
        from: firstOfMonth,
        to: today,
        product_code: "",
      },

      // Stats
      stats: [],
      statsLoading: false,

      // Report table
      rows: [],
      reportLoading: false,
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 25,
        total: 0,
      },

      // Export
      exporting: false,

      // Toast
      toast: { show: false, message: "", type: "success" },

      cardAccents: ["#067169", "#0d9488", "#0f766e", "#14b8a6", "#2dd4bf"],
    };
  },

  computed: {
    totalClicks() {
      return this.stats.reduce((sum, s) => sum + Number(s.total_clicks), 0);
    },
    productOptions() {
      return this.stats.map((s) => ({ code: s.product_code, name: s.product_name }));
    },
    visiblePages() {
      const { current_page, last_page } = this.pagination;
      const pages = [];
      if (last_page <= 7) {
        for (let i = 1; i <= last_page; i++) pages.push(i);
      } else {
        pages.push(1);
        if (current_page > 3) pages.push("...");
        for (let i = Math.max(2, current_page - 1); i <= Math.min(last_page - 1, current_page + 1); i++) {
          pages.push(i);
        }
        if (current_page < last_page - 2) pages.push("...");
        pages.push(last_page);
      }
      return pages;
    },
  },

  mounted() {
    this.fetchAll();
  },

  methods: {
    base() {
      return process.env.VUE_APP_BACKEND;
    },

    authHeader() {
      const token = localStorage.getItem("access_token");
      return token ? { Authorization: `Bearer ${token}` } : {};
    },

    async fetchAll() {
      await Promise.all([this.fetchStats(), this.fetchReport(1)]);
    },

    // ── Stats cards ────────────────────────────────────────────────
    async fetchStats() {
      this.statsLoading = true;
      try {
        const { data } = await axios.get(`${this.base()}/api/v1/admin/ipd/stats`, {
          headers: this.authHeader(),
          params: { from: this.filters.from, to: this.filters.to },
        });
        this.stats = data.data;
      } catch {
        this.showToast("Failed to load stats.", "error");
      } finally {
        this.statsLoading = false;
      }
    },

    // ── Click log table ────────────────────────────────────────────
    async fetchReport(page = 1) {
      this.reportLoading = true;
      try {
        const { data } = await axios.get(`${this.base()}/api/v1/admin/ipd/report`, {
          headers: this.authHeader(),
          params: {
            from: this.filters.from,
            to: this.filters.to,
            product_code: this.filters.product_code || undefined,
            page,
            per_page: this.pagination.per_page,
          },
        });
        this.rows = data.data;
        this.pagination = {
          current_page: data.current_page,
          last_page:    data.last_page,
          per_page:     data.per_page,
          total:        data.total,
        };
      } catch {
        this.showToast("Failed to load report.", "error");
      } finally {
        this.reportLoading = false;
      }
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      this.fetchReport(page);
    },

    // ── Excel export ───────────────────────────────────────────────
    async exportExcel() {
      this.exporting = true;
      try {
        const response = await axios.get(`${this.base()}/api/v1/admin/ipd/export`, {
          headers: this.authHeader(),
          params: {
            from: this.filters.from,
            to:   this.filters.to,
            product_code: this.filters.product_code || undefined,
          },
          responseType: "blob",
        });

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href  = URL.createObjectURL(blob);
        link.download = `ipd_report_${this.filters.from}_to_${this.filters.to}.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
        this.showToast("Export downloaded successfully.", "success");
      } catch {
        this.showToast("Export failed. Please try again.", "error");
      } finally {
        this.exporting = false;
      }
    },

    // ── Filters ────────────────────────────────────────────────────
    resetFilters() {
      const today = new Date().toISOString().slice(0, 10);
      const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
        .toISOString().slice(0, 10);
      this.filters = { from: firstOfMonth, to: today, product_code: "" };
      this.fetchAll();
    },

    // ── Helpers ────────────────────────────────────────────────────
    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },

    truncate(str, len) {
      if (!str) return "—";
      return str.length > len ? str.slice(0, len) + "…" : str;
    },

    showToast(message, type = "success") {
      this.toast = { show: true, message, type };
      setTimeout(() => { this.toast.show = false; }, 3500);
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

* { box-sizing: border-box; }

.report-page {
  font-family: 'DM Sans', sans-serif;
  background: #f4f6f5;
  min-height: 100vh;
  padding: 32px 28px 60px;
  color: #1a2e2c;
}

/* ── Header ── */
.report-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
}
.header-eyebrow {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #067169;
  margin-bottom: 4px;
}
.header-title {
  font-size: 1.9rem;
  font-weight: 600;
  margin: 0;
  color: #0d2825;
  letter-spacing: -0.02em;
}
.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #067169;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
}
.export-btn:hover:not(:disabled) { background: #055c54; transform: translateY(-1px); }
.export-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Filters ── */
.filters-bar {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 28px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.filter-group { display: flex; flex-direction: column; gap: 5px; }
.filter-label { font-size: 0.72rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: #6b8885; }
.filter-input {
  border: 1.5px solid #d4e2e0;
  border-radius: 7px;
  padding: 7px 12px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.875rem;
  color: #1a2e2c;
  background: #f9fbfb;
  outline: none;
  transition: border-color 0.15s;
  min-width: 140px;
}
.filter-input:focus { border-color: #067169; background: #fff; }
.reset-btn {
  background: none;
  border: 1.5px solid #d4e2e0;
  border-radius: 7px;
  padding: 7px 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.875rem;
  color: #6b8885;
  cursor: pointer;
  transition: all 0.15s;
  align-self: flex-end;
}
.reset-btn:hover { border-color: #067169; color: #067169; }

/* ── Stat Cards ── */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}
.stat-card {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  border-left: 4px solid var(--accent, #067169);
  transition: transform 0.15s, box-shadow 0.15s;
}
.stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.09); }
.stat-card--total { border-style: solid; background: #f0faf9; }
.stat-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
.stat-product { font-size: 0.82rem; font-weight: 600; color: #1a2e2c; }
.stat-code { font-size: 0.7rem; font-family: 'DM Mono', monospace; color: #9bb5b2; background: #f0f5f4; padding: 2px 6px; border-radius: 4px; }
.stat-number { font-size: 2.2rem; font-weight: 600; color: var(--accent, #067169); letter-spacing: -0.03em; line-height: 1; }
.stat-label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.08em; color: #9bb5b2; margin: 4px 0 10px; }
.stat-meta { display: flex; justify-content: space-between; font-size: 0.72rem; color: #9bb5b2; }

.stats-skeleton { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 16px; margin-bottom: 28px; }
.skeleton-card { height: 130px; background: linear-gradient(90deg, #eef2f1 25%, #e4ecea 50%, #eef2f1 75%); background-size: 200% 100%; animation: shimmer 1.4s infinite; border-radius: 14px; }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* ── Table ── */
.table-section {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  overflow: hidden;
}
.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #edf2f1;
}
.table-title { font-size: 1rem; font-weight: 600; margin: 0; color: #0d2825; }
.table-count { font-size: 0.8rem; color: #9bb5b2; background: #f0f5f4; padding: 3px 10px; border-radius: 20px; }

.table-wrap { position: relative; overflow-x: auto; }
.table-loading-overlay {
  position: absolute; inset: 0;
  background: rgba(255,255,255,0.7);
  display: flex; align-items: center; justify-content: center;
  z-index: 10;
}

.data-table { width: 100%; border-collapse: collapse; font-size: 0.845rem; }
.data-table thead tr { background: #f4f8f7; }
.data-table th {
  text-align: left;
  padding: 11px 16px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #6b8885;
  white-space: nowrap;
  border-bottom: 1px solid #edf2f1;
}
.data-table td {
  padding: 11px 16px;
  border-bottom: 1px solid #f0f5f4;
  color: #2d4a47;
  vertical-align: middle;
}
.row-even td { background: #fafcfc; }
.data-table tbody tr:hover td { background: #f0faf9; }

.td-num { color: #9bb5b2; font-family: 'DM Mono', monospace; font-size: 0.78rem; }
.td-date { white-space: nowrap; font-size: 0.82rem; }
.td-mono { font-family: 'DM Mono', monospace; font-size: 0.8rem; color: #4a6d6a; }
.td-agent { max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 0.78rem; color: #9bb5b2; }

.product-badge {
  background: #e8f5f4;
  color: #067169;
  font-size: 0.72rem;
  font-weight: 600;
  font-family: 'DM Mono', monospace;
  padding: 3px 8px;
  border-radius: 5px;
}
.lang-badge {
  background: #f0f5f4;
  color: #4a6d6a;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 5px;
  text-transform: uppercase;
}
.user-info { display: flex; flex-direction: column; gap: 1px; }
.user-name  { font-weight: 500; font-size: 0.82rem; }
.user-email { font-size: 0.72rem; color: #9bb5b2; }
.guest-label { color: #b8cbc9; font-style: italic; font-size: 0.82rem; }
.empty-row { text-align: center; padding: 48px !important; color: #9bb5b2; font-style: italic; }

/* ── Pagination ── */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 18px 24px;
  border-top: 1px solid #edf2f1;
  flex-wrap: wrap;
}
.page-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1.5px solid #d4e2e0;
  border-radius: 7px;
  background: #fff;
  color: #2d4a47;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.12s;
}
.page-btn:hover:not(:disabled) { border-color: #067169; color: #067169; background: #f0faf9; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-btn--active { background: #067169; border-color: #067169; color: #fff; font-weight: 600; }
.page-ellipsis { color: #9bb5b2; padding: 0 4px; }

/* ── Toast ── */
.toast {
  position: fixed;
  bottom: 28px;
  right: 28px;
  padding: 12px 20px;
  border-radius: 9px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #fff;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  z-index: 9999;
}
.toast--success { background: #067169; }
.toast--error   { background: #c0392b; }
.toast-fade-enter-active, .toast-fade-leave-active { transition: opacity 0.3s, transform 0.3s; }
.toast-fade-enter-from, .toast-fade-leave-to { opacity: 0; transform: translateY(10px); }

/* ── Spinners ── */
.spinner-sm {
  display: inline-block;
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
.spinner-lg {
  display: inline-block;
  width: 32px; height: 32px;
  border: 3px solid #d4e2e0;
  border-top-color: #067169;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Responsive ── */
@media (max-width: 768px) {
  .report-page { padding: 20px 16px 40px; }
  .header-title { font-size: 1.4rem; }
  .filters-bar { flex-direction: column; align-items: stretch; }
  .filter-input, .reset-btn { width: 100%; }
}
</style>